import React, { useEffect, useState, useCallback } from 'react';
import AddUserModal from './AddUserModal';
import EditUserModal from './EditUserModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import '../css/UserManagement.css';
import {
  FaTrash,
  FaSync,
  FaPlus,
  FaSearch,
  FaEye,
  FaEdit,
  FaChevronLeft,
  FaChevronRight,
  FaFilePdf,
  FaFileExcel
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { signup, getAllAccounts, deleteAccount, updateAccount } from '../../../../api/authApi';
import { useTranslation } from 'react-i18next';

const UserManagement = () => {
  const { t } = useTranslation();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('Status');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAllAccounts();
      if (Array.isArray(response)) {
        const mappedUsers = response.map(acc => ({
          id: acc.accId,
          name: acc.user?.userName || 'Unknown',
          email: acc.email,
          phone: acc.phoneNumber || 'N/A',
          role: acc.role,
          status: acc.user?.active
            ? t('admin.user_management.active')
            : t('admin.user_management.inactive'),
          avatar: acc.user?.avatar || 'https://cdn-icons-png.flaticon.com/512/149/149071.png',
        }));
        setUsers(mappedUsers);
      } else {
        console.error('Invalid response:', response);
      }
    } catch (error) {
      console.error('Failed to load user list:', error);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleAddUser = async (userData) => {
    try {
      const payload = {
        email: userData.email,
        password: userData.password,
        phoneNumber: userData.phone,
        role: userData.role,
        user: {
          userName: userData.name,
          avatar: userData.avatar,
          active: userData.status
        }
      };
      await signup(payload);
      await fetchUsers();
      setIsAddModalOpen(false);
      alert(t('admin.user_management.messages.add_success'));
    } catch (err) {
      console.error('Add user failed:', err);
      alert(t('admin.user_management.messages.add_error'));
    }
  };

  const handleEditUser = async (formData) => {
    try {
      const payload = {
        email: formData.email,
        phoneNumber: formData.phone,
        role: formData.role,
        user: {
          userName: formData.name,
          avatar: formData.avatar,
          active: formData.status
        }
      };
      await updateAccount(selectedUser.id, payload);
      await fetchUsers();
      setIsEditModalOpen(false);
      setSelectedUser(null);
      alert(t('admin.user_management.messages.update_success'));
    } catch (error) {
      console.error('Edit user failed:', error);
      alert(t('admin.user_management.messages.update_error'));
    }
  };

  const handleDeleteUser = async () => {
    try {
      if (!selectedUser) return;
      await deleteAccount(selectedUser.id);
      await fetchUsers();
      alert(t('admin.user_management.messages.delete_success'));
    } catch (err) {
      console.error('Delete failed:', err);
      alert(t('admin.user_management.messages.delete_error'));
    } finally {
      setIsDeleteModalOpen(false);
      setSelectedUser(null);
    }
  };

  const handleViewUser = (user) => {
    alert(`${t('admin.user_management.user_name')}: ${user.name}\n${t('admin.user_management.email')}: ${user.email}\n${t('admin.user_management.role')}: ${user.role}\n${t('admin.user_management.status')}: ${user.status}`);
  };

  const handleRefresh = async () => {
    await fetchUsers();
    setSearchTerm('');
    setStatusFilter('Status');
    setCurrentPage(1);
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.phone.includes(searchTerm);
    const matchesStatus = statusFilter === 'Status' || user.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + rowsPerPage);

  const exportToPDF = () => {
    const doc = new jsPDF();
    autoTable(doc, {
      head: [[
        t('admin.user_management.table.user_name'),
        t('admin.user_management.table.phone'),
        t('admin.user_management.table.email'),
        t('admin.user_management.table.role'),
        t('admin.user_management.table.status')
      ]],
      body: filteredUsers.map(user => [
        user.name, user.phone, user.email, user.role, user.status
      ]),
      startY: 40,
      styles: { fontSize: 10, cellPadding: 3 },
      headStyles: { fillColor: [245, 158, 11], textColor: [255, 255, 255] },
    });
    doc.setFontSize(20);
    doc.text(t('admin.user_management.title'), 14, 22);
    doc.setFontSize(12);
    doc.text(`${t('reports.charts.total_bookings')}: ${filteredUsers.length}`, 14, 32);
    doc.save('PhoViet-UserList.pdf');
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      filteredUsers.map(user => ({
        [t('admin.user_management.table.user_name')]: user.name,
        [t('admin.user_management.table.phone')]: user.phone,
        [t('admin.user_management.table.email')]: user.email,
        [t('admin.user_management.table.role')]: user.role,
        [t('admin.user_management.table.status')]: user.status
      }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Users');
    XLSX.writeFile(workbook, 'PhoViet-UserList.xlsx');
  };

  if (loading) return <p style={{ textAlign: 'center' }}>{t('admin.user_management.loading')}</p>;

  return (
    <div className="user-management">
      {/* Header */}
      <div className="header">
        <div className="header-left">
          <h1>{t('admin.user_management.title')}</h1>
          <p>{t('admin.user_management.subtitle')}</p>
        </div>
        <div className="header-right">
          <button className="icon-button pdf" onClick={exportToPDF} title={t('admin.user_management.export.pdf')}>
            <FaFilePdf />
          </button>
          <button className="icon-button excel" onClick={exportToExcel} title={t('admin.user_management.export.excel')}>
            <FaFileExcel />
          </button>
          <button className="icon-button refresh" onClick={handleRefresh} title={t('admin.user_management.refresh')}>
            <FaSync />
          </button>
          <button className="add-user-btn" onClick={() => setIsAddModalOpen(true)}>
            <FaPlus /> {t('admin.user_management.add_user')}
          </button>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="controls">
        <div className="search-container">
          <FaSearch className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder={t('admin.user_management.search_placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select
          className="status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="Status">{t('admin.user_management.status')} ▼</option>
          <option value={t('admin.user_management.active')}>{t('admin.user_management.active')}</option>
          <option value={t('admin.user_management.inactive')}>{t('admin.user_management.inactive')}</option>
        </select>
      </div>

      {/* User Table */}
      <div className="table-container">
        <table className="user-table">
          <thead>
            <tr>
              <th>{t('admin.user_management.table.user_name')}</th>
              <th>{t('admin.user_management.table.phone')}</th>
              <th>{t('admin.user_management.table.email')}</th>
              <th>{t('admin.user_management.table.role')}</th>
              <th>{t('admin.user_management.table.status')}</th>
              <th>{t('admin.user_management.table.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {paginatedUsers.map(user => (
              <tr key={user.id}>
                <td>
                  <div className="user-info">
                    <img src={user.avatar} alt={user.name} className="user-avatar" />
                    <span className="user-name">{user.name}</span>
                  </div>
                </td>
                <td>{user.phone}</td>
                <td>{user.email}</td>
                <td>{user.role}</td>
                <td>
                  <span className={`user-status-badge ${user.status.toLowerCase()}`}>
                    {user.status}
                  </span>
                </td>
                <td>
                  <div className="actions">
                    <button className="action-btn" onClick={() => handleViewUser(user)} title={t('admin.user_management.actions.view')}>
                      <FaEye />
                    </button>
                    <button className="action-btn" onClick={() => { setSelectedUser(user); setIsEditModalOpen(true); }} title={t('admin.user_management.actions.update')}>
                      <FaEdit />
                    </button>
                    <button className="action-btn" onClick={() => { setSelectedUser(user); setIsDeleteModalOpen(true); }} title={t('admin.user_management.actions.delete')}>
                      <FaTrash />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="pagination">
          <div className="pagination-left">
            <span>{t('admin.user_management.pagination.row_per_page')}</span>
            <select
              className="rows-per-page"
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
            </select>
            <span>{t('admin.user_management.pagination.entries')}</span>
          </div>
          <div className="pagination-right">
            <button className="page-btn" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}>
              <FaChevronLeft />
            </button>
            <button className={`page-btn ${currentPage === 1 ? 'active' : ''}`}>{currentPage}</button>
            <button className="page-btn" onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages}>
              <FaChevronRight />
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AddUserModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onSubmit={handleAddUser} />
      {selectedUser && (
        <EditUserModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedUser(null);
          }}
          onSubmit={handleEditUser}
          user={selectedUser}
        />
      )}
      {selectedUser && (
        <DeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setSelectedUser(null);
          }}
          onConfirm={handleDeleteUser}
          userName={selectedUser.name}
        />
      )}
    </div>
  );
};

export default UserManagement;
